import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

// ── Status presentation ───────────────────────────────────────────────────────
// 'pending' = no value recorded yet, 'unset' = metric has no baseline configured.
// Both are deliberately grey: a colour with nothing behind it is worse than none.
const STATUS = {
  green:   { label: 'Green',       colour: '#15803D', text: '#FFFFFF' },
  amber:   { label: 'Amber',       colour: '#B07820', text: '#FFFFFF' },
  red:     { label: 'Red',         colour: '#C0392B', text: '#FFFFFF' },
  pending: { label: 'Not entered', colour: '#E5E7EB', text: '#6B7280' },
  unset:   { label: 'No baseline', colour: '#E5E7EB', text: '#6B7280' },
};

const statusOf = (s) => STATUS[s] || STATUS.pending;

// The four content-cadence commitments, in the order the doc lists them.
const CADENCE_ITEMS = [
  { key: 'blog',      label: 'Blog post' },
  { key: 'qa',        label: 'Q&A' },
  { key: 'instagram', label: 'Instagram post' },
  { key: 'facebook',  label: 'Facebook post' },
];

const SOURCE_LABEL = {
  manual:   'Manual entry',
  internal: 'Auto — from our own order data',
  shopify:  'Auto — Shopify API',
};

// ── Date helpers ──────────────────────────────────────────────────────────────
const shiftWeek = (iso, weeks) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
};

const formatWeek = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatWeekRange = (iso) => {
  if (!iso) return '';
  const end   = new Date(`${iso}T00:00:00`);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  const opts = { day: '2-digit', month: 'short' };
  return `${start.toLocaleDateString('en-NZ', opts)} – ${end.toLocaleDateString('en-NZ', opts)}`;
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

// ── Value formatting ──────────────────────────────────────────────────────────
const formatValue = (row) => {
  if (row.value === null || row.value === undefined) return '—';
  const n = Number(row.value);
  if (row.unit === '$') return `$${n.toFixed(2)}`;
  if (row.unit === '%') return `${n.toFixed(1)}%`;
  if (row.rule_type === 'count_missed') {
    return n === 0 ? 'All published' : `${n} missed`;
  }
  return String(n);
};

// Adverse variance vs baseline, as the traffic light sees it.
const varianceText = (row) => {
  if (row.rule_type === 'count_missed') return null;
  if (row.baseline === null || row.baseline === undefined) return 'no baseline set';
  if (row.value === null || row.value === undefined) return null;
  const base = Number(row.baseline);
  if (!base) return 'no baseline set';
  const val = Number(row.value);
  const adverse = row.direction === 'lower_better'
    ? ((val - base) / base) * 100
    : ((base - val) / base) * 100;
  const baseFmt = row.unit === '$' ? `$${base.toFixed(2)}` : `${base}${row.unit === '%' ? '%' : ''}`;
  if (Math.abs(adverse) < 0.05) return `on baseline (${baseFmt})`;
  return adverse > 0
    ? `${adverse.toFixed(1)}% adverse vs ${baseFmt}`
    : `${Math.abs(adverse).toFixed(1)}% better than ${baseFmt}`;
};

// ═════════════════════════════════════════════════════════════════════════════
// Weekly report tab
// ═════════════════════════════════════════════════════════════════════════════
const WeeklyReport = ({ rows, history, week, initials, onSave, saving }) => {
  const [drafts, setDrafts] = useState({});
  const [notes, setNotes]   = useState({});

  // Reset any half-typed entry whenever the week changes.
  useEffect(() => { setDrafts({}); setNotes({}); }, [week]);

  const historyFor = useCallback(
    (key) => history.filter(h => h.metric_key === key),
    [history]
  );

  const cadenceState = (row) => {
    const detail = row.detail || {};
    // Nothing recorded yet: default every box to unticked.
    return CADENCE_ITEMS.reduce((acc, item) => ({
      ...acc,
      [item.key]: detail[item.key] === true,
    }), {});
  };

  const handleCadenceToggle = (row, itemKey) => {
    const next = { ...cadenceState(row), [itemKey]: !cadenceState(row)[itemKey] };
    const missed = CADENCE_ITEMS.filter(i => !next[i.key]).length;
    onSave(row.metric_key, missed, next, notes[row.metric_key] ?? row.note ?? null);
  };

  const handleValueSave = (row) => {
    const raw = drafts[row.metric_key];
    if (raw === undefined || raw === '') return;
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    onSave(row.metric_key, num, null, notes[row.metric_key] ?? row.note ?? null);
    setDrafts(d => ({ ...d, [row.metric_key]: undefined }));
  };

  return (
    <div>
      {rows.map((row) => {
        const st   = statusOf(row.status);
        const vari = varianceText(row);
        const hist = historyFor(row.metric_key);
        const editable = row.source !== 'internal';

        return (
          <div
            key={row.metric_key}
            style={{
              display: 'flex', alignItems: 'stretch', gap: 16,
              padding: '14px 16px', borderBottom: '1px solid var(--border)',
            }}
          >
            {/* ── Label + definition ─────────────────────────────────────── */}
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>
                {row.label}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3, lineHeight: 1.45 }}>
                {row.definition}
              </div>
              <div style={{ fontSize: 10, color: 'var(--muted2)', marginTop: 4 }}>
                {SOURCE_LABEL[row.source] || row.source}
                {row.entered_by ? ` · entered by ${row.entered_by}` : ''}
                {row.computed_at ? ` · ${formatDateTime(row.computed_at)}` : ''}
              </div>

              {/* ── Entry controls ──────────────────────────────────────── */}
              {editable && row.rule_type === 'count_missed' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10 }}>
                  {CADENCE_ITEMS.map(item => (
                    <label
                      key={item.key}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        fontSize: 12, color: 'var(--white)', cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={cadenceState(row)[item.key]}
                        disabled={saving}
                        onChange={() => handleCadenceToggle(row, item.key)}
                      />
                      {item.label}
                    </label>
                  ))}
                </div>
              )}

              {editable && row.rule_type !== 'count_missed' && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={row.value !== null ? String(row.value) : `Enter ${row.unit === '$' ? 'value' : 'percentage'}`}
                    value={drafts[row.metric_key] ?? ''}
                    onChange={e => setDrafts(d => ({ ...d, [row.metric_key]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleValueSave(row); }}
                    style={{
                      width: 130, background: 'var(--bg)', border: '1px solid var(--border2)',
                      borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
                      fontSize: 12, padding: '5px 8px', outline: 'none',
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Note (optional)"
                    value={notes[row.metric_key] ?? row.note ?? ''}
                    onChange={e => setNotes(n => ({ ...n, [row.metric_key]: e.target.value }))}
                    style={{
                      flex: 1, maxWidth: 320, background: 'var(--bg)',
                      border: '1px solid var(--border2)', borderRadius: 4,
                      color: 'var(--white)', fontFamily: 'var(--font-body)',
                      fontSize: 12, padding: '5px 8px', outline: 'none',
                    }}
                  />
                  <button
                    className="action-btn action-btn--close"
                    style={{ fontSize: 10, padding: '4px 12px' }}
                    disabled={saving || drafts[row.metric_key] === undefined || drafts[row.metric_key] === ''}
                    onClick={() => handleValueSave(row)}
                  >
                    ✓ Save
                  </button>
                </div>
              )}

              {!editable && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, fontStyle: 'italic' }}>
                  Calculated automatically each time this page loads — nothing to enter.
                </div>
              )}
            </div>

            {/* ── Trend strip ────────────────────────────────────────────── */}
            <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 3 }}>
              {hist.map(h => (
                <div
                  key={h.week_ending}
                  title={`Week ending ${formatWeek(h.week_ending)} — ${statusOf(h.status).label}${h.value !== null ? ` (${h.value})` : ''}`}
                  style={{
                    width: 8, height: 26, borderRadius: 2,
                    background: statusOf(h.status).colour,
                    border: h.week_ending === week ? '1px solid var(--navy)' : 'none',
                  }}
                />
              ))}
            </div>

            {/* ── Value + colour block ───────────────────────────────────── */}
            <div style={{ flex: '0 0 210px', textAlign: 'right' }}>
              <div style={{
                fontSize: 20, fontWeight: 700, color: 'var(--white)',
                fontFamily: 'var(--font-mono)',
              }}>
                {formatValue(row)}
              </div>
              {vari && (
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{vari}</div>
              )}
              <div style={{
                marginTop: 8, background: st.colour, color: st.text,
                borderRadius: 4, padding: '8px 0', fontSize: 12,
                fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>
                {st.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Critical error log tab
// ═════════════════════════════════════════════════════════════════════════════
const RESOLVE_TARGET_HOURS = 48;

const hoursBetween = (from, to) =>
  (new Date(to).getTime() - new Date(from).getTime()) / 3600000;

const CriticalErrors = ({ errors, staff, initials, onAdd, onResolve, saving }) => {
  const [errorType, setErrorType]     = useState('');
  const [description, setDescription] = useState('');
  const [showResolved, setShowResolved] = useState(false);

  const visible = errors.filter(e => showResolved || !e.resolved_at);

  const submit = () => {
    if (!description.trim()) return;
    onAdd(errorType.trim() || null, description.trim());
    setErrorType('');
    setDescription('');
  };

  return (
    <div>
      {/* ── Log a new error ─────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center',
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg3)',
      }}>
        <input
          type="text"
          placeholder="Error type — e.g. stock status mismatch"
          value={errorType}
          onChange={e => setErrorType(e.target.value)}
          style={{
            flex: '0 0 260px', background: 'var(--bg2)', border: '1px solid var(--border2)',
            borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
            fontSize: 12, padding: '6px 10px', outline: 'none',
          }}
        />
        <input
          type="text"
          placeholder="What is wrong? (required)"
          value={description}
          onChange={e => setDescription(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          style={{
            flex: 1, background: 'var(--bg2)', border: '1px solid var(--border2)',
            borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
            fontSize: 12, padding: '6px 10px', outline: 'none',
          }}
        />
        <button
          className="action-btn action-btn--close"
          style={{ fontSize: 10, padding: '5px 14px', whiteSpace: 'nowrap' }}
          disabled={saving || !description.trim() || !initials}
          onClick={submit}
          title={!initials ? 'Choose who you are first' : undefined}
        >
          + Flag Error
        </button>
        <button
          className="action-btn action-btn--ghost"
          style={{ fontSize: 10, padding: '5px 12px', whiteSpace: 'nowrap' }}
          onClick={() => setShowResolved(v => !v)}
        >
          {showResolved ? 'Hide Resolved' : 'Show Resolved'}
        </button>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Error Type</th>
              <th>Description</th>
              <th>Flagged</th>
              <th>Resolved</th>
              <th className="num">Time to Resolve</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={6} className="empty-row" style={{ textAlign: 'center', padding: 20 }}>
                  No critical errors outstanding.
                </td>
              </tr>
            )}
            {visible.map(e => {
              const open  = !e.resolved_at;
              const hours = hoursBetween(e.flagged_at, e.resolved_at || new Date().toISOString());
              const breach = hours > RESOLVE_TARGET_HOURS;
              return (
                <tr key={e.id}>
                  <td>{e.error_type || '—'}</td>
                  <td>
                    {e.description}
                    {e.logged_by && (
                      <span style={{ color: 'var(--muted2)', fontSize: 11 }}> · {e.logged_by}</span>
                    )}
                  </td>
                  <td className="muted">{formatDateTime(e.flagged_at)}</td>
                  <td className="muted">
                    {e.resolved_at ? formatDateTime(e.resolved_at) : <em>Open</em>}
                    {e.resolved_by && (
                      <span style={{ color: 'var(--muted2)', fontSize: 11 }}> · {e.resolved_by}</span>
                    )}
                  </td>
                  <td className="num" style={{ color: breach ? 'var(--neg)' : undefined, fontWeight: breach ? 700 : 400 }}>
                    {hours.toFixed(1)} hrs{open ? ' so far' : ''}
                  </td>
                  <td>
                    {open ? (
                      <button
                        className="action-btn action-btn--close"
                        style={{ fontSize: 10, padding: '3px 10px' }}
                        disabled={saving || !initials}
                        onClick={() => onResolve(e.id)}
                        title={!initials ? 'Choose who you are first' : undefined}
                      >
                        ✓ Mark Resolved
                      </button>
                    ) : (
                      <span style={{ color: 'var(--muted2)', fontSize: 11 }}>Closed</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Settings tab — baselines and thresholds
// ═════════════════════════════════════════════════════════════════════════════
const Settings = ({ rows, onSaveConfig, saving }) => {
  const [edits, setEdits] = useState({});

  const fieldValue = (row, field) => {
    const e = edits[row.metric_key];
    if (e && e[field] !== undefined) return e[field];
    return row[field] === null || row[field] === undefined ? '' : String(row[field]);
  };

  const setField = (key, field, value) =>
    setEdits(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));

  const save = (row) => {
    const e = edits[row.metric_key];
    if (!e) return;
    const payload = {};
    if (e.baseline !== undefined)        payload.baseline        = e.baseline === '' ? null : Number(e.baseline);
    if (e.green_threshold !== undefined) payload.green_threshold = Number(e.green_threshold);
    if (e.amber_threshold !== undefined) payload.amber_threshold = Number(e.amber_threshold);
    if (!Object.keys(payload).length) return;
    onSaveConfig(row.metric_key, payload);
    setEdits(prev => ({ ...prev, [row.metric_key]: undefined }));
  };

  return (
    <div>
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        fontSize: 11, color: 'var(--muted)', lineHeight: 1.6, background: 'var(--bg3)',
      }}>
        <strong style={{ color: 'var(--white)' }}>Baselines are the placeholders that still need your numbers.</strong>{' '}
        Until a baseline is set, that metric shows grey rather than a colour. Thresholds below are the
        draft cutoffs from the requirements doc — change them here and every week on record re-colours
        immediately, including history. Thresholds are <em>adverse</em> deviation as a percentage of
        baseline: green up to the first number, amber up to the second, red beyond it. Content Cadence
        is the exception — its numbers are counts of missed items, not percentages.
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Better When</th>
              <th className="num">Baseline</th>
              <th className="num">Green up to</th>
              <th className="num">Amber up to</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const dirty = !!edits[row.metric_key];
              const isCount = row.rule_type === 'count_missed';
              return (
                <tr key={row.metric_key}>
                  <td className="bold">
                    {row.label}
                    <div style={{ fontSize: 10, color: 'var(--muted2)', fontWeight: 400 }}>
                      {SOURCE_LABEL[row.source] || row.source}
                    </div>
                  </td>
                  <td className="muted">
                    {isCount ? 'Fewer missed' : row.direction === 'lower_better' ? 'Lower' : 'Higher'}
                  </td>
                  <td className="num">
                    {isCount ? (
                      <span style={{ color: 'var(--muted2)' }}>n/a</span>
                    ) : (
                      <input
                        type="number"
                        step="0.01"
                        placeholder="not set"
                        value={fieldValue(row, 'baseline')}
                        onChange={e => setField(row.metric_key, 'baseline', e.target.value)}
                        style={{
                          width: 90, background: 'var(--bg)', border: '1px solid var(--border2)',
                          borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-mono)',
                          fontSize: 12, padding: '4px 6px', textAlign: 'right', outline: 'none',
                        }}
                      />
                    )}
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      step="0.1"
                      value={fieldValue(row, 'green_threshold')}
                      onChange={e => setField(row.metric_key, 'green_threshold', e.target.value)}
                      style={{
                        width: 70, background: 'var(--bg)', border: '1px solid var(--border2)',
                        borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-mono)',
                        fontSize: 12, padding: '4px 6px', textAlign: 'right', outline: 'none',
                      }}
                    />
                    <span style={{ color: 'var(--muted2)', fontSize: 11, marginLeft: 4 }}>
                      {isCount ? 'missed' : '%'}
                    </span>
                  </td>
                  <td className="num">
                    <input
                      type="number"
                      step="0.1"
                      value={fieldValue(row, 'amber_threshold')}
                      onChange={e => setField(row.metric_key, 'amber_threshold', e.target.value)}
                      style={{
                        width: 70, background: 'var(--bg)', border: '1px solid var(--border2)',
                        borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-mono)',
                        fontSize: 12, padding: '4px 6px', textAlign: 'right', outline: 'none',
                      }}
                    />
                    <span style={{ color: 'var(--muted2)', fontSize: 11, marginLeft: 4 }}>
                      {isCount ? 'missed' : '%'}
                    </span>
                  </td>
                  <td>
                    <button
                      className={`action-btn ${dirty ? 'action-btn--close' : 'action-btn--ghost'}`}
                      style={{ fontSize: 10, padding: '3px 10px' }}
                      disabled={!dirty || saving}
                      onClick={() => save(row)}
                    >
                      ✓ Save
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Page
// ═════════════════════════════════════════════════════════════════════════════
const HISTORY_WEEKS = 12;

const DigitalMetrics = () => {
  const [tab, setTab]         = useState('report');
  const [week, setWeek]       = useState(null);   // null = let the RPC pick this Friday
  const [rows, setRows]       = useState([]);
  const [history, setHistory] = useState([]);
  const [errors, setErrors]   = useState([]);
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [message, setMessage] = useState(null);

  // Auth is two shared logins, so there is no per-person identity to read.
  // Attribution comes from an explicit "who are you" pick, remembered locally —
  // the same approach BlockedOrders uses for its staff notes.
  const [initials, setInitials] = useState(
    () => window.localStorage.getItem('digitalMetricsInitials') || ''
  );

  const chooseInitials = (v) => {
    setInitials(v);
    if (v) window.localStorage.setItem('digitalMetricsInitials', v);
    else   window.localStorage.removeItem('digitalMetricsInitials');
  };

  const loadWeek = useCallback(async (targetWeek) => {
    setLoading(true);
    const [{ data: weekRows, error: weekErr }, { data: histRows }] = await Promise.all([
      supabase.rpc('get_digital_metrics_week', { p_week_ending: targetWeek }),
      supabase.rpc('get_digital_metrics_history', { p_weeks: HISTORY_WEEKS }),
    ]);

    if (weekErr) {
      setMessage(`Could not load metrics: ${weekErr.message}`);
      setLoading(false);
      return;
    }

    setRows(weekRows || []);
    // The RPC resolves "this Friday" for us — adopt whatever week it reported.
    if (weekRows && weekRows.length) setWeek(weekRows[0].week_ending);
    // Oldest first, so the trend strip reads left to right.
    setHistory((histRows || []).slice().sort(
      (a, b) => new Date(a.week_ending) - new Date(b.week_ending)
    ));
    setLoading(false);
  }, []);

  const loadSupporting = useCallback(async () => {
    const [{ data: staffRows }, { data: errorRows }] = await Promise.all([
      supabase.from('staff').select('id, name, initials').order('initials'),
      supabase
        .from('digital_critical_errors')
        .select('id, error_type, description, flagged_at, resolved_at, logged_by, resolved_by, notes')
        .order('flagged_at', { ascending: false })
        .limit(200),
    ]);
    setStaff(staffRows || []);
    setErrors(errorRows || []);
  }, []);

  // Both loaders are stable (useCallback with no deps), so this runs once on
  // mount. Passing null lets the RPC resolve "this Friday" itself.
  useEffect(() => { loadWeek(null); loadSupporting(); }, [loadWeek, loadSupporting]);

  const goWeek = (delta) => {
    const next = shiftWeek(week, delta);
    setWeek(next);
    loadWeek(next);
  };

  // ── Writes ────────────────────────────────────────────────────────────────
  const handleSaveValue = async (metricKey, value, detail, note) => {
    if (!initials) {
      setMessage('Choose who you are before entering a value.');
      return;
    }
    setSaving(true);
    const { error } = await supabase.rpc('upsert_digital_metric_value', {
      p_metric_key:  metricKey,
      p_week_ending: week,
      p_value:       value,
      p_detail:      detail,
      p_entered_by:  initials,
      p_note:        note || null,
      p_source_used: 'manual',
    });
    setSaving(false);
    if (error) { setMessage(`Save failed: ${error.message}`); return; }
    setMessage(null);
    loadWeek(week);
  };

  const handleSaveConfig = async (metricKey, payload) => {
    setSaving(true);
    const { error } = await supabase
      .from('digital_metrics')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('metric_key', metricKey);
    setSaving(false);
    if (error) { setMessage(`Save failed: ${error.message}`); return; }
    setMessage(null);
    loadWeek(week);
  };

  const handleAddError = async (errorType, description) => {
    setSaving(true);
    const { error } = await supabase.from('digital_critical_errors').insert({
      error_type:  errorType,
      description,
      logged_by:   initials || null,
      flagged_at:  new Date().toISOString(),
    });
    setSaving(false);
    if (error) { setMessage(`Could not log error: ${error.message}`); return; }
    setMessage(null);
    loadSupporting();
  };

  const handleResolveError = async (id) => {
    setSaving(true);
    const { error } = await supabase
      .from('digital_critical_errors')
      .update({
        resolved_at: new Date().toISOString(),
        resolved_by: initials || null,
        updated_at:  new Date().toISOString(),
      })
      .eq('id', id);
    setSaving(false);
    if (error) { setMessage(`Could not resolve: ${error.message}`); return; }
    setMessage(null);
    loadSupporting();
  };

  // ── Summary counts for the header ─────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { green: 0, amber: 0, red: 0, grey: 0 };
    for (const r of rows) {
      if (r.status === 'green')      c.green += 1;
      else if (r.status === 'amber') c.amber += 1;
      else if (r.status === 'red')   c.red += 1;
      else                            c.grey += 1;
    }
    return c;
  }, [rows]);

  const openErrors = errors.filter(e => !e.resolved_at).length;

  const TabButton = ({ id, children }) => (
    <button
      className={`action-btn ${tab === id ? 'action-btn--close' : 'action-btn--ghost'}`}
      style={{ fontSize: 10, padding: '4px 12px' }}
      onClick={() => setTab(id)}
    >
      {children}
    </button>
  );

  return (
    <main className="app-main">
      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">

          {/* ── Header ───────────────────────────────────────────────────── */}
          <div className="dash-panel__header">
            <span className="dash-panel__title">
              📈 Digital Metrics
              {openErrors > 0 && (
                <span style={{
                  marginLeft: 8, background: '#C0392B', color: '#fff',
                  borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
                }}>
                  {openErrors} open error{openErrors === 1 ? '' : 's'}
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>Entering as</span>
              <select
                value={initials}
                onChange={e => chooseInitials(e.target.value)}
                style={{
                  background: 'var(--bg)', border: '1px solid var(--border2)',
                  borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
                  fontSize: 12, padding: '4px 8px', outline: 'none',
                }}
              >
                <option value="">— choose —</option>
                {staff.map(s => (
                  <option key={s.id} value={s.initials}>
                    {s.initials} — {s.name}
                  </option>
                ))}
              </select>
              <button
                className="dash-refresh-btn"
                onClick={() => { loadWeek(week); loadSupporting(); }}
                disabled={loading}
              >
                ↻
              </button>
            </div>
          </div>

          {/* ── Tabs + week navigation ───────────────────────────────────── */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
          }}>
            <TabButton id="report">Weekly Report</TabButton>
            <TabButton id="errors">
              Critical Errors{openErrors > 0 ? ` (${openErrors})` : ''}
            </TabButton>
            <TabButton id="settings">Baselines &amp; Thresholds</TabButton>

            {tab === 'report' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
                <button
                  className="action-btn action-btn--ghost"
                  style={{ fontSize: 10, padding: '4px 10px' }}
                  onClick={() => goWeek(-1)}
                  disabled={loading || !week}
                >
                  ◀ Previous week
                </button>
                <div style={{ textAlign: 'center', minWidth: 190 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--white)' }}>
                    Week ending {formatWeek(week)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>{formatWeekRange(week)}</div>
                </div>
                <button
                  className="action-btn action-btn--ghost"
                  style={{ fontSize: 10, padding: '4px 10px' }}
                  onClick={() => goWeek(1)}
                  disabled={loading || !week}
                >
                  Next week ▶
                </button>
              </div>
            )}
          </div>

          {/* ── Status summary ───────────────────────────────────────────── */}
          {tab === 'report' && !loading && (
            <div style={{
              display: 'flex', gap: 16, alignItems: 'center',
              padding: '10px 16px', borderBottom: '1px solid var(--border)',
              fontSize: 12, color: 'var(--muted)',
            }}>
              <span><strong style={{ color: '#15803D' }}>{counts.green}</strong> green</span>
              <span><strong style={{ color: '#B07820' }}>{counts.amber}</strong> amber</span>
              <span><strong style={{ color: '#C0392B' }}>{counts.red}</strong> red</span>
              {counts.grey > 0 && (
                <span style={{ color: 'var(--muted2)' }}>
                  {counts.grey} awaiting a value or a baseline
                </span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted2)' }}>
                Trend strip shows the last {HISTORY_WEEKS} weeks, oldest on the left.
              </span>
            </div>
          )}

          {message && (
            <div style={{
              padding: '10px 16px', borderBottom: '1px solid var(--border)',
              fontSize: 12, color: 'var(--neg)', background: '#FEF2F2',
            }}>
              {message}
            </div>
          )}

          {/* ── Body ─────────────────────────────────────────────────────── */}
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>
          ) : tab === 'report' ? (
            <WeeklyReport
              rows={rows}
              history={history}
              week={week}
              initials={initials}
              onSave={handleSaveValue}
              saving={saving}
            />
          ) : tab === 'errors' ? (
            <CriticalErrors
              errors={errors}
              staff={staff}
              initials={initials}
              onAdd={handleAddError}
              onResolve={handleResolveError}
              saving={saving}
            />
          ) : (
            <Settings rows={rows} onSaveConfig={handleSaveConfig} saving={saving} />
          )}
        </div>
      </div>
    </main>
  );
};

export default DigitalMetrics;
